import React, { useState, useEffect } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "react-toastify";
import userService from "../../../services/userServices";
import logger from '../../../utils/logger';

// Helper function to format datetime to mm/dd/yyyy hh:mm AM/PM for display
const formatDateTimeForDisplay = (datetimeString) => {
  if (!datetimeString) return "";

  const date = new Date(datetimeString);

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
};

const WebinarForm = ({
  meetingName,
  setMeetingName,
  meetingStartTime,
  setMeetingStartTime,
  meetingEndTime,
  setMeetingEndTime,
  invitesEmailInput,
  setInvitesEmailInput,
  invitesList,
  setInvitesList,
  roomSettings,
  handleSettingChange,
  customBackgroundEnabled,
  setCustomBackgroundEnabled,
  backgroundType,
  setBackgroundType,
  backgroundColor,
  setBackgroundColor,
  gradientColor1,
  setGradientColor1,
  gradientColor2,
  setGradientColor2,
  gradientDirection,
  setGradientDirection,
  backgroundImage,
  setBackgroundImage,
  backgroundImagePreview,
  setBackgroundImagePreview,
  fileInputRef,
  exitUrlEnabled,
  setExitUrlEnabled,
  exitUrl,
  setExitUrl,
  customButtonEnabled,
  setCustomButtonEnabled,
  customButtonUrl,
  setCustomButtonUrl,
  customButtonText,
  setCustomButtonText,
  waitingRoomTextEnabled,
  setWaitingRoomTextEnabled,
  waitingRoomTitle,
  setWaitingRoomTitle,
  waitingRoomMessage,
  setWaitingRoomMessage,
  waitingRoomSubText,
  setWaitingRoomSubText,
  isValidUrl,
  handleStartMeeting,
}) => {
  // Co-Host state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Speakers state
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [speakersList, setSpeakersList] = useState([]);
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] =
    useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  // Common email domains for suggestions
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  // Generate suggestions based on input (Co-Host)
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }

    setSelectedSuggestionIndex(-1);

    // Debug logging
    logger("log", "Co-Host suggestions:", {
      input,
      newSuggestions,
      showSuggestions: newSuggestions.length > 0,
      suggestions: newSuggestions.slice(0, 5),
    });
  }, [invitesEmailInput, invitesList]);

  // Check if user exists when valid email is entered (Co-Host)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setUserExists(false);
          } else {
            setUserExists(false);
            logger("error", "Error checking user:", error);
          }
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Generate speaker suggestions based on input
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    if (emailRegex.test(input)) {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    } else {
      setSpeakerSuggestions(newSuggestions.slice(0, 5));
    }

    if (newSuggestions.length > 0) {
      setShowSpeakerSuggestions(true);
    } else {
      setShowSpeakerSuggestions(false);
    }

    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if speaker user exists when valid email is entered
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setSpeakerUserExists(false);
          } else {
            setSpeakerUserExists(false);
            logger("error", "Error checking speaker user:", error);
          }
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  // Handle adding co-host (existing user or invite)
  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          logger("error", "Error checking user:", error);
        }
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  // Handle adding speaker (existing user or invite)
  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // If isInvite is null, we need to check if user exists
    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          logger("error", "Error checking speaker user:", error);
        }
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  return (
    <div className="rounded-xl border border-gray-600 p-8 primary-card-background">
      {/* Meeting Name and Your Name */}
      <div className="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-white text-sm font-medium mb-2">
            Webinar Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={meetingName}
            onChange={(e) => setMeetingName(e.target.value)}
            placeholder="Enter webinar name"
            maxLength={25}
            className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
              meetingName.trim() && meetingName.trim().length < 3
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-600 focus:ring-indigo-500"
            }`}
          />
          <p className="text-gray-400 text-xs mt-1">
            {meetingName.trim() && meetingName.trim().length < 3 ? (
              <span className="text-red-400">
                Minimum 3 characters required
              </span>
            ) : (
              "25 characters max"
            )}
          </p>
        </div>
      </div>

      {/* Webinar Settings */}
      <div className="mb-8">
        <h3 className="text-white text-lg font-semibold mb-6">
          WEBINAR SETTINGS
        </h3>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Webinar Start Time
            </label>
            <div className="relative">
              <input
                type="text"
                value={meetingStartTime}
                onChange={(e) => setMeetingStartTime(e.target.value)}
                placeholder="MM/DD/YYYY HH:MM AM/PM"
                className="w-full px-4 py-3 pr-12 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="datetime-local"
                step="900"
                onChange={(e) => {
                  if (e.target.value) {
                    const formatted = formatDateTimeForDisplay(e.target.value);
                    setMeetingStartTime(formatted);
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 opacity-0 cursor-pointer"
                title="Pick a date and time (15-minute intervals)"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-1">
              Format: MM/DD/YYYY HH:MM AM/PM (e.g., 12/25/2024 2:30 PM)
            </p>
            <p className="text-gray-400 text-xs mt-0.5">
              Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </p>
          </div>
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Webinar End Time
            </label>
            <div className="relative">
              <input
                type="text"
                value={meetingEndTime}
                onChange={(e) => setMeetingEndTime(e.target.value)}
                placeholder="MM/DD/YYYY HH:MM AM/PM"
                className="w-full px-4 py-3 pr-12 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="datetime-local"
                step="900"
                onChange={(e) => {
                  if (e.target.value) {
                    const formatted = formatDateTimeForDisplay(e.target.value);
                    setMeetingEndTime(formatted);
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 opacity-0 cursor-pointer"
                title="Pick a date and time (15-minute intervals)"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <p className="text-gray-400 text-xs mt-1">
              Format: MM/DD/YYYY HH:MM AM/PM (e.g., 12/25/2024 2:30 PM)
            </p>
            <p className="text-gray-400 text-xs mt-0.5">
              Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </p>
          </div>
        </div>

        {/* Co-Host */}
        <div className="mb-8">
          <h3 className="text-white text-lg font-semibold mb-4">Co-Host</h3>
          <div className="relative" style={{ overflow: "visible" }}>
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 relative" style={{ overflow: "visible" }}>
                <input
                  type="email"
                  value={invitesEmailInput}
                  onChange={(e) => setInvitesEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        selectedSuggestionIndex >= 0 &&
                        suggestions[selectedSuggestionIndex]
                      ) {
                        e.preventDefault();
                        const selectedEmail =
                          suggestions[selectedSuggestionIndex];
                        handleAddCoHost(selectedEmail, null);
                        setSelectedSuggestionIndex(-1);
                      } else if (invitesEmailInput.trim() && !checkingUser) {
                        e.preventDefault();
                        const email = invitesEmailInput.trim();
                        // Always check user existence first
                        handleAddCoHost(email, null);
                      }
                    } else if (
                      e.key === "ArrowDown" &&
                      suggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev < suggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === "ArrowUp" && suggestions.length > 0) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      setSelectedSuggestionIndex((prev) =>
                        prev > 0 ? prev - 1 : 0
                      );
                    } else if (e.key === "Escape") {
                      setShowSuggestions(false);
                      setSelectedSuggestionIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    if (invitesEmailInput.trim().length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Add co-host email and press Enter"
                  className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {/* User Status Indicator */}
                {invitesEmailInput.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingUser ? (
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : userExists === true ? (
                      <div className="text-green-500 text-sm">✓</div>
                    ) : userExists === false ? (
                      <Mail className="w-5 h-5 text-orange-500" />
                    ) : null}
                  </div>
                )}
                {/* Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div
                    className="absolute z-[9999] w-full mt-1 bg-white border-2 border-blue-500 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                    }}
                  >
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          handleAddCoHost(suggestion, null);
                        }}
                        className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                          selectedSuggestionIndex === index
                            ? "bg-indigo-100"
                            : ""
                        }`}
                      >
                        <div className="text-sm text-gray-700">
                          {suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Debug: Always show if suggestions exist */}
                {suggestions.length > 0 && (
                  <div className="text-xs text-red-400 mt-1">
                    DEBUG: {suggestions.length} suggestions, showSuggestions:{" "}
                    {showSuggestions ? "true" : "false"}
                  </div>
                )}
                {/* User Exists/Invite Options */}
                {!showSuggestions &&
                  invitesEmailInput.trim() &&
                  userExists !== null &&
                  !checkingUser && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {userExists ? (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), false)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-green-500">✓</div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User exists - Add as Co-Host
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleAddCoHost(invitesEmailInput.trim(), true)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {invitesEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User not found - Send Email Invite
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={async () => {
                  if (invitesEmailInput.trim()) {
                    const email = invitesEmailInput.trim();
                    // Always check user existence first
                    await handleAddCoHost(email, null);
                  }
                }}
                disabled={checkingUser}
                className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingUser ? "..." : "Add"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {invitesList.map((email, index) => {
              const isInvite = inviteEmails.includes(email);
              return (
                <span
                  key={index}
                  className={`flex items-center px-3 py-1 rounded-full text-sm ${
                    isInvite
                      ? "bg-orange-600 text-white"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {isInvite && <Mail className="w-3 h-3 mr-1" />}
                  {email}
                  <button
                    onClick={() => {
                      setInvitesList((prev) =>
                        prev.filter((item) => item !== email)
                      );
                      setInviteEmails((prev) =>
                        prev.filter((item) => item !== email)
                      );
                    }}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        {/* Speakers */}
        <div className="mb-8">
          <h3 className="text-white text-lg font-semibold mb-4">Speakers</h3>
          <div className="relative" style={{ overflow: "visible" }}>
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex-1 relative" style={{ overflow: "visible" }}>
                <input
                  type="email"
                  value={speakersEmailInput}
                  onChange={(e) => setSpeakersEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (
                        selectedSpeakerSuggestionIndex >= 0 &&
                        speakerSuggestions[selectedSpeakerSuggestionIndex]
                      ) {
                        e.preventDefault();
                        const selectedEmail =
                          speakerSuggestions[selectedSpeakerSuggestionIndex];
                        handleAddSpeaker(selectedEmail, null);
                        setSelectedSpeakerSuggestionIndex(-1);
                      } else if (
                        speakersEmailInput.trim() &&
                        !checkingSpeakerUser
                      ) {
                        e.preventDefault();
                        const email = speakersEmailInput.trim();
                        // Always check user existence first
                        handleAddSpeaker(email, null);
                      }
                    } else if (
                      e.key === "ArrowDown" &&
                      speakerSuggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSpeakerSuggestions(true);
                      setSelectedSpeakerSuggestionIndex((prev) =>
                        prev < speakerSuggestions.length - 1 ? prev + 1 : prev
                      );
                    } else if (
                      e.key === "ArrowUp" &&
                      speakerSuggestions.length > 0
                    ) {
                      e.preventDefault();
                      setShowSpeakerSuggestions(true);
                      setSelectedSpeakerSuggestionIndex((prev) =>
                        prev > 0 ? prev - 1 : 0
                      );
                    } else if (e.key === "Escape") {
                      setShowSpeakerSuggestions(false);
                      setSelectedSpeakerSuggestionIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    if (speakersEmailInput.trim().length > 0) {
                      setShowSpeakerSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSpeakerSuggestions(false), 200);
                  }}
                  placeholder="Add speaker email and press Enter"
                  className="w-full px-4 py-3 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {/* Speaker User Status Indicator */}
                {speakersEmailInput.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingSpeakerUser ? (
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : speakerUserExists === true ? (
                      <div className="text-green-500 text-sm">✓</div>
                    ) : speakerUserExists === false ? (
                      <Mail className="w-5 h-5 text-orange-500" />
                    ) : null}
                  </div>
                )}
                {/* Speaker Suggestions Dropdown */}
                {showSpeakerSuggestions && speakerSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {speakerSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          handleAddSpeaker(suggestion, null);
                        }}
                        className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                          selectedSpeakerSuggestionIndex === index
                            ? "bg-indigo-100"
                            : ""
                        }`}
                      >
                        <div className="text-sm text-gray-700">
                          {suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Speaker User Exists/Invite Options */}
                {!showSpeakerSuggestions &&
                  speakersEmailInput.trim() &&
                  speakerUserExists !== null &&
                  !checkingSpeakerUser && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      {speakerUserExists ? (
                        <div
                          onClick={() =>
                            handleAddSpeaker(speakersEmailInput.trim(), false)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <div className="text-green-500">✓</div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {speakersEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User exists - Add as Speaker
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleAddSpeaker(speakersEmailInput.trim(), true)
                          }
                          className="px-4 py-3 cursor-pointer hover:bg-orange-50 border-b border-gray-200"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {speakersEmailInput.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                User not found - Send Email Invite
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <button
                onClick={async () => {
                  if (speakersEmailInput.trim()) {
                    const email = speakersEmailInput.trim();
                    // Always check user existence first
                    await handleAddSpeaker(email, null);
                  }
                }}
                disabled={checkingSpeakerUser}
                className="px-4 py-3 bg-[#6A1FBF] text-white rounded-lg hover:bg-[#6A1FBF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingSpeakerUser ? "..." : "Add"}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {speakersList.map((email, index) => {
              const isInvite = speakerInviteEmails.includes(email);
              return (
                <span
                  key={index}
                  className={`flex items-center px-3 py-1 rounded-full text-sm ${
                    isInvite
                      ? "bg-orange-600 text-white"
                      : "bg-gray-700 text-white"
                  }`}
                >
                  {isInvite && <Mail className="w-3 h-3 mr-1" />}
                  {email}
                  <button
                    onClick={() => {
                      setSpeakersList((prev) =>
                        prev.filter((item) => item !== email)
                      );
                      setSpeakerInviteEmails((prev) =>
                        prev.filter((item) => item !== email)
                      );
                    }}
                    className="ml-2 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        <div className="space-y-6">
          {/* Allow Chat */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Chat</h4>
              <p className="text-gray-400 text-sm">
                (Enable or disable in-webinar chat for participants.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowChat", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
              <button
                onClick={() => handleSettingChange("allowChat", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowChat
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
            </div>
          </div>

          {/* Allow Reactions */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Reactions</h4>
              <p className="text-gray-400 text-sm">
                (Participants can use emoji reactions during the webinar.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowReactions", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
              <button
                onClick={() => handleSettingChange("allowReactions", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowReactions
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
            </div>
          </div>

          {/* Allow Raise Hand */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Allow Raise Hand</h4>
              <p className="text-gray-400 text-sm">
                (Participants can raise their hand to request attention from the
                host.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("allowRaiseHand", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
              <button
                onClick={() => handleSettingChange("allowRaiseHand", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.allowRaiseHand
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
            </div>
          </div>

          {/* Require Password */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Require Password</h4>
              <p className="text-gray-400 text-sm">
                (Participants will need to enter a password to join the
                webinar.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSettingChange("requirePassword", true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
              <button
                onClick={() => handleSettingChange("requirePassword", false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.requirePassword
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
            </div>
          </div>

          {/* Password Input */}
          {roomSettings.requirePassword && (
            <div className="pl-8">
              <input
                type="text"
                value={roomSettings.password}
                onChange={(e) =>
                  handleSettingChange("password", e.target.value)
                }
                placeholder="Enter password"
                className="w-full max-w-xs px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* Waiting Room */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-white font-medium">Waiting Room</h4>
              <p className="text-gray-400 text-sm">
                (Participants will be placed in a waiting room before joining
                the webinar.)
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() =>
                  handleSettingChange("hostApprovalRequired", true)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  roomSettings.hostApprovalRequired
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                On
              </button>
              <button
                onClick={() =>
                  handleSettingChange("hostApprovalRequired", false)
                }
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !roomSettings.hostApprovalRequired
                    ? "bg-[#6A1FBF] text-white"
                    : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                }`}
              >
                Off
              </button>
            </div>
          </div>

          {/* Exit URL */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Exit URL</h4>
                <p className="text-gray-400 text-sm">
                  (Redirect participants to a specific URL when they leave the
                  webinar.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setExitUrlEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    exitUrlEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => {
                    setExitUrlEnabled(false);
                    setExitUrl("");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !exitUrlEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
              </div>
            </div>

            {exitUrlEnabled && (
              <div className="pl-4">
                <label className="block text-white text-sm font-medium mb-2">
                  Exit URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={exitUrl}
                  onChange={(e) => setExitUrl(e.target.value)}
                  placeholder="https://example.com"
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                    exitUrl && !isValidUrl(exitUrl)
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-600 focus:ring-indigo-500"
                  }`}
                />
                <p className="text-gray-400 text-xs mt-1">
                  {exitUrl && !isValidUrl(exitUrl) ? (
                    <span className="text-red-400">
                      Please enter a valid URL
                    </span>
                  ) : (
                    "Enter a valid URL (e.g., https://example.com)"
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Custom Button */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Custom Button</h4>
                <p className="text-gray-400 text-sm">
                  (Add a custom button with a URL link.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setCustomButtonEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    customButtonEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => {
                    setCustomButtonEnabled(false);
                    setCustomButtonUrl("");
                    setCustomButtonText("");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !customButtonEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
              </div>
            </div>

            {customButtonEnabled && (
              <div className="pl-4 space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Button Text <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customButtonText}
                    onChange={(e) => setCustomButtonText(e.target.value)}
                    placeholder="e.g., Visit our website"
                    maxLength={50}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      customButtonText.trim() &&
                      customButtonText.trim().length < 3
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-indigo-500"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {customButtonText.trim() &&
                    customButtonText.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${customButtonText.length}/50 characters`
                    )}
                  </p>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Button URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customButtonUrl}
                    onChange={(e) => setCustomButtonUrl(e.target.value)}
                    placeholder="https://example.com"
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      customButtonUrl && !isValidUrl(customButtonUrl)
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-indigo-500"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {customButtonUrl && !isValidUrl(customButtonUrl) ? (
                      <span className="text-red-400">
                        Please enter a valid URL
                      </span>
                    ) : (
                      "Enter a valid URL (e.g., https://example.com)"
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Waiting Room Text */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Waiting Room Text</h4>
                <p className="text-gray-400 text-sm">
                  (Customize the waiting room message for participants.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setWaitingRoomTextEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    waitingRoomTextEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => {
                    setWaitingRoomTextEnabled(false);
                    setWaitingRoomTitle("");
                    setWaitingRoomMessage("");
                    setWaitingRoomSubText("");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !waitingRoomTextEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
              </div>
            </div>

            {waitingRoomTextEnabled && (
              <div className="pl-4 space-y-4">
                {/* Title Field */}
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={waitingRoomTitle}
                    onChange={(e) => setWaitingRoomTitle(e.target.value)}
                    placeholder="e.g., Please wait"
                    maxLength={100}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      waitingRoomTitle.trim() &&
                      waitingRoomTitle.trim().length < 3
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-indigo-500"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {waitingRoomTitle.trim() &&
                    waitingRoomTitle.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomTitle.length}/100 characters`
                    )}
                  </p>
                </div>

                {/* Message Field */}
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={waitingRoomMessage}
                    onChange={(e) => setWaitingRoomMessage(e.target.value)}
                    placeholder="e.g., The host will admit you shortly"
                    maxLength={200}
                    rows={3}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      waitingRoomMessage.trim() &&
                      waitingRoomMessage.trim().length < 3
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-indigo-500"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {waitingRoomMessage.trim() &&
                    waitingRoomMessage.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomMessage.length}/200 characters`
                    )}
                  </p>
                </div>

                {/* Sub Text Field */}
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Sub Text <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={waitingRoomSubText}
                    onChange={(e) => setWaitingRoomSubText(e.target.value)}
                    placeholder="e.g., You will be automatically admitted once approved"
                    maxLength={150}
                    className={`w-full px-4 py-3 bg-white border rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${
                      waitingRoomSubText.trim() &&
                      waitingRoomSubText.trim().length < 3
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-600 focus:ring-indigo-500"
                    }`}
                  />
                  <p className="text-gray-400 text-xs mt-1">
                    {waitingRoomSubText.trim() &&
                    waitingRoomSubText.trim().length < 3 ? (
                      <span className="text-red-400">
                        Minimum 3 characters required
                      </span>
                    ) : (
                      `${waitingRoomSubText.length}/150 characters`
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Custom Background */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-white font-medium">Custom Background</h4>
                <p className="text-gray-400 text-sm">
                  (Add a custom background to your webinar room.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setCustomBackgroundEnabled(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => {
                    setCustomBackgroundEnabled(false);
                    // Clear background image when turning off
                    setBackgroundImage(null);
                    setBackgroundImagePreview(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !customBackgroundEnabled
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
              </div>
            </div>

            {customBackgroundEnabled && (
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setBackgroundType("solid")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "solid"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Solid Color
                  </button>
                  <button
                    onClick={() => setBackgroundType("gradient")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "gradient"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Gradient
                  </button>
                  <button
                    onClick={() => setBackgroundType("image")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      backgroundType === "image"
                        ? "bg-[#6A1FBF] text-white"
                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                    }`}
                  >
                    Image
                  </button>
                </div>

                {/* Solid Color Picker */}
                {backgroundType === "solid" && (
                  <div className="pl-4">
                    <label className="block text-white text-sm font-medium mb-2">
                      Background Color
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        placeholder="#1a1a2e"
                        className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div
                        className="w-20 h-10 rounded border border-gray-600"
                        style={{ backgroundColor }}
                      />
                    </div>
                  </div>
                )}

                {/* Gradient Color Picker */}
                {backgroundType === "gradient" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        First Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor1}
                          onChange={(e) => setGradientColor1(e.target.value)}
                          placeholder="#1a1a2e"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Second Color
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          className="w-16 h-10 rounded border border-gray-600 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={gradientColor2}
                          onChange={(e) => setGradientColor2(e.target.value)}
                          placeholder="#615fff"
                          className="px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Gradient Direction
                      </label>
                      <select
                        value={gradientDirection}
                        onChange={(e) => setGradientDirection(e.target.value)}
                        className="w-full px-4 py-2 bg-white border border-gray-600 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="to right">Left to Right</option>
                        <option value="to left">Right to Left</option>
                        <option value="to bottom">Top to Bottom</option>
                        <option value="to top">Bottom to Top</option>
                        <option value="to bottom right">
                          Top Left to Bottom Right
                        </option>
                        <option value="to bottom left">
                          Top Right to Bottom Left
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Preview
                      </label>
                      <div
                        className="w-full h-20 rounded border border-gray-600"
                        style={{
                          background: `linear-gradient(${gradientDirection}, ${gradientColor1}, ${gradientColor2})`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Image Upload */}
                {backgroundType === "image" && (
                  <div className="pl-4 space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Background Image
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validate file size (5MB max)
                            const maxSize = 5 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast.error("Image size should not exceed 5MB");
                              e.target.value = "";
                              return;
                            }
                            setBackgroundImage(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setBackgroundImagePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-400
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-[#6A1FBF] file:text-white
                        hover:file:bg-indigo-700
                        file:cursor-pointer cursor-pointer"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        Maximum file size: 5MB
                      </p>
                      {backgroundImage && (
                        <p className="text-green-400 text-xs mt-1">
                          Selected: {backgroundImage.name} (
                          {(backgroundImage.size / 1024).toFixed(2)} KB)
                        </p>
                      )}
                    </div>

                    {backgroundImagePreview && (
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Preview
                        </label>
                        <div className="relative w-full h-64 rounded-lg overflow-hidden border border-gray-600 bg-gray-800">
                          <img
                            src={backgroundImagePreview}
                            alt="Background preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            onClick={() => {
                              setBackgroundImage(null);
                              setBackgroundImagePreview(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-1"
                          >
                            <X size={16} />
                            <span className="text-sm">Remove</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save as Preset */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-white font-medium">Save as Preset</h4>
                <p className="text-gray-400 text-sm">
                  (Save these settings as a preset for future webinars.)
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleSettingChange("saveAsPreset", true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    roomSettings.saveAsPreset
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => {
                    handleSettingChange("saveAsPreset", false);
                    handleSettingChange("presetName", "");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !roomSettings.saveAsPreset
                      ? "bg-[#6A1FBF] text-white"
                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                  }`}
                >
                  Off
                </button>
              </div>
            </div>
          </div>

          {/* Preset Name Input */}
          {roomSettings.saveAsPreset && (
            <div className="pl-8">
              <input
                type="text"
                value={roomSettings.presetName}
                onChange={(e) =>
                  handleSettingChange("presetName", e.target.value)
                }
                placeholder="Enter preset name"
                className="w-full max-w-xs px-4 py-2 bg-white border border-gray-600 rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Start Webinar Button */}
      <div className="flex justify-end">
        <button
          onClick={() => handleStartMeeting(true)}
          className="bg-[#6A1FBF] text-white font-bold py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors mr-2"
        >
          Start Now
        </button>
        <button
          onClick={() => handleStartMeeting(false)}
          className="bg-[#6A1FBF] text-white font-bold py-3 px-8 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Schedule Webinar
        </button>
      </div>
    </div>
  );
};

export default WebinarForm;
